/* Outbox flush + reconnect wiring (PWA polish P2). Imports the raw (non-
   enqueuing) network calls and replays queued ops in order when online. */
import { getQueue, removeOp, bumpTries, deadLetter, migrateLegacyQueue } from "./outbox";
import { getUid } from "./supabase";
import { upsertShoeRaw, requestValidationRaw } from "./shoes";
import { recordDrillRaw } from "./drillStats";
import { recordSpeedRaw } from "./speedStats";
import { recordCardValsRaw } from "./cardvalStats";
import { markLessonReadRaw } from "./curriculumProgress";

const HANDLERS = {
  shoe: (p) => upsertShoeRaw(p),
  drill: (p) => recordDrillRaw(p.deltas, p.day),
  speed: (p) => recordSpeedRaw(p.system, p.cpm, p.correct, p.day),
  cardval: (p) => recordCardValsRaw(p.deltas),
  curriculum: (p) => markLessonReadRaw(p.stepId),
  // Only ever enqueued after a successful online completion save (see
  // requestValidation) — outbox-replayed shoes still never request
  // validation, preserving the offline threat model.
  validate: (p) => requestValidationRaw(p.shoeId).then((d) => d != null),
};

/* Flush stops at the first failure to preserve oldest-first ordering, which
   means a permanently-rejected op (lapsed Pro gating an RPC, an RLS
   rejection, a payload the server now refuses) would wedge everything behind
   it forever. After MAX_TRIES failures — each from a distinct flush attempt
   while the browser believed it was online — the op is dead-lettered and the
   queue moves on. Transient outages stay safe: flush doesn't run offline, so
   a long flight can't burn through the cap. */
const MAX_TRIES = 8;

let flushing = false;

/* Replay queued writes oldest-first. Stops at the first (presumed transient)
   failure and keeps the rest for the next attempt. Each op is removed only
   after its handler confirms success, so a mid-flush drop just retries later. */
export async function flushOutbox() {
  if (flushing) return;
  if (typeof navigator !== "undefined" && !navigator.onLine) return;
  flushing = true;
  try {
    const uid = await getUid();
    if (!uid) return; // signed out — queues are per-uid and stay parked
    migrateLegacyQueue(uid);
    // Web Locks make the flush single-flight across tabs, so two tabs can't
    // both replay the same additive drill/speed delta (double-counting).
    // Falls back to the per-tab `flushing` flag where unsupported.
    if (typeof navigator !== "undefined" && navigator.locks?.request) {
      await navigator.locks.request(
        "byb.outbox.flush",
        { ifAvailable: true },
        (lock) => (lock ? flushQueue(uid) : undefined),
      );
    } else {
      await flushQueue(uid);
    }
  } finally {
    flushing = false;
  }
}

async function flushQueue(uid) {
  for (const op of getQueue(uid)) {
    // A successful direct write may have dropped this op mid-flush
    // (removeOps in upsertShoe) — re-check before replaying stale state.
    if (!getQueue(uid).some((o) => o.id === op.id)) continue;
    let ok;
    try {
      const h = HANDLERS[op.kind];
      ok = h ? await h(op.payload) : true; // unknown kind → drop
    } catch {
      ok = false;
    }
    if (ok) {
      removeOp(op.id, uid);
    } else if (bumpTries(op.id, uid) >= MAX_TRIES) {
      deadLetter(op.id, uid);
      continue; // unwedged — the rest of the queue can proceed
    } else {
      break; // presumed transient — retry the same op next flush
    }
  }
}

/* Wire reconnect + visibility + a startup flush (a queue may survive a reload). */
export function initSync() {
  if (typeof window === "undefined") return;
  window.addEventListener("online", flushOutbox);
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") flushOutbox();
  });
  // A freshly-enqueued op (failed write or validation request mid-session)
  // retries after a short delay instead of waiting for the next online/
  // visibility event. flushOutbox is single-flight, so overlapping timers
  // from a burst of enqueues are harmless.
  window.addEventListener("byb:outbox-enqueued", () => setTimeout(flushOutbox, 15000));
  flushOutbox();
}
